/**
 * The identity providers core-api can sign a user in with. Each has a
 * `GET /auth/<provider>` route that runs the OAuth dance and returns the
 * browser to `next`.
 */
export const SIGN_IN_PROVIDERS = ["google", "microsoft"] as const;

export type SignInProvider = (typeof SIGN_IN_PROVIDERS)[number];

export function isSignInProvider(
  candidate: string | string[] | undefined,
): candidate is SignInProvider {
  return (
    typeof candidate === "string" &&
    (SIGN_IN_PROVIDERS as readonly string[]).includes(candidate)
  );
}

/**
 * Query params the provider button adds to the return URL so the welcome
 * page knows how the user got there and what they answered on the way.
 */
export const SIGN_IN_PARAMS = {
  /** The provider the sign-in went through: one of {@link SIGN_IN_PROVIDERS}. */
  via: "via",
  /**
   * `1` or `0` when a newsletter checkbox stood next to the button, absent
   * when the page showed none.
   */
  newsletter: "newsletter",
  /**
   * A one-time token minted on click and kept in this tab's sessionStorage.
   * The welcome page only trusts the params above when the token in the URL
   * is the one it holds; a link someone else crafted cannot carry it.
   */
  state: "sid",
  /**
   * `1` when the sign-in created the account. Not ours: core-api adds it to
   * the return URL before redirecting, being the only party that knows. A
   * forged one can at most show the welcome prompt.
   */
  signup: "signup",
  /**
   * The page the button was on (`/login` or `/signup`), so a refused sign-in
   * can go back there with the reason.
   */
  from: "from",
  /**
   * Why the sign-in was refused, one of {@link SIGN_IN_ERROR_CODES}. Not
   * ours either: core-api sends the user back with it instead of answering
   * with the error, which would strand them on the API's JSON.
   */
  error: "error",
} as const;

/** The reasons core-api gives for refusing a provider sign-in. */
export const SIGN_IN_ERROR_CODES = [
  "no-verified-email",
  "address-linked-elsewhere",
  "address-taken",
  "cancelled",
  "provider-error",
] as const;

export type SignInErrorCode = (typeof SIGN_IN_ERROR_CODES)[number];

export function isSignInErrorCode(
  candidate: string | string[] | undefined,
): candidate is SignInErrorCode {
  return (
    typeof candidate === "string" &&
    (SIGN_IN_ERROR_CODES as readonly string[]).includes(candidate)
  );
}

const STORAGE_KEY = "picter:sign-in-state";

/**
 * Mints the token for a sign-in this tab is about to start and remembers it.
 * sessionStorage survives the round trip through the provider within the tab
 * (the UTM tracking relies on the same property). Returns null when storage
 * is unavailable; the welcome page will then ignore the sign-in params.
 */
export function createSignInState(): string | null {
  const state =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2) + Date.now().toString(36);

  try {
    sessionStorage.setItem(STORAGE_KEY, state);
    return state;
  } catch {
    return null;
  }
}

/**
 * Whether `candidate` is the token this tab minted. Does not consume it: the
 * welcome page clears it with {@link clearSignInState} once it has applied
 * the params, so a failed attempt can be retried by refreshing.
 */
export function isSignInState(
  candidate: string | string[] | undefined,
): boolean {
  if (typeof candidate !== "string" || candidate === "") {
    return false;
  }

  try {
    return sessionStorage.getItem(STORAGE_KEY) === candidate;
  } catch {
    return false;
  }
}

export function clearSignInState(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // nothing to clear
  }
}
